export type OpenCuePayload = {
  message: string;
  openMethod?: string;
  /**
   * Seed `message` without drawing a user bubble for it, for a CTA whose message
   * is an internal brief the recruiter never typed. Rides on Cue's own
   * `UserMessage.hidden` flag, which the chat already honours — the agent still
   * receives the text.
   *
   * The suppression is local to this browser tab and nothing more. `hidden`
   * never leaves the client: Cue's SSE payload builder sends only `content` and
   * `attachments` per message, and fountain-ai's request schema and message
   * table have no field for it. So the text is persisted as an ordinary user
   * message and it surfaces three ways.
   *
   * 1. It becomes the thread's `lastUserMessage`, which renders in the
   *    chat-history sidebar right away. This one is not deferred to a reload.
   * 2. Reloading the thread draws it as a real user bubble. The `/ui` history
   *    projection drops rows whose `ui_message` is null, and a plain text
   *    message from a user role is not one of them.
   * 3. A reload that lands mid-stream synthesizes a user bubble from that same
   *    `lastUserMessage`.
   *
   * Suppressing it for real needs the server side: a client-reachable marker on
   * fountain-ai's message request schema, honoured where `inputProvenance`
   * already nulls `ui_message` at persist time, plus a rule for the thread
   * preview. Nothing today lets a client ask for that. So write any message
   * passed with `hidden` as text that is safe for the recruiter to read.
   */
  hidden?: boolean;
};

export type CopilotCommandDetail = {
  type: string;
  payload: Record<string, unknown>;
  /**
   * Forwarded as the second argument to the copilot's `sendMessageToQueue`
   * (see WxCopilot/README.md). `sentFrom` attributes the resulting
   * `cue:conversation_message_send` / `cue:conversation_first_message_send`
   * event to the surface that opened the conversation.
   */
  options?: Record<string, unknown>;
};

export function dispatchCopilotCommand(detail: CopilotCommandDetail): void {
  window.dispatchEvent(
    new CustomEvent('wx-copilot-command', {
      detail,
      bubbles: true,
      composed: true,
    }),
  );
}

export function dispatchOpenCue({
  message,
  openMethod = 'external_open_cue',
  hidden = false,
}: OpenCuePayload): void {
  if (!hidden) {
    dispatchCopilotCommand({
      type: 'openCue',
      payload: { message, openMethod },
    });
    return;
  }

  // Two commands, because `openCue` always renders the message it is given and
  // takes no flag to suppress that. So open the panel with no message, then
  // queue the text through the command Cue's own in-chat components use for
  // machine-written turns. Cue buffers a queued message until the chat stream
  // mounts, which is the same thing `openCue` relies on for its own send.
  dispatchCopilotCommand({
    type: 'openCue',
    payload: { openMethod },
  });
  dispatchCopilotCommand({
    type: 'sendMessageToQueue',
    payload: { type: 'user', data: message, hidden: true },
    options: { sentFrom: openMethod },
  });
}
